import { useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import type { MessageItem } from 'tripPlanner/components/Chat/MessagesList'
import type { MessagePart } from 'tripPlanner/components/Chat/Message'
import { stripMarkdown } from 'lib/string/stripMarkdown'

type UseClipboardArgs = {
  lastAssistantMessage?: MessageItem
  isDevMode: boolean
}

interface ClipboardLabels {
  options: string
}

function serializeUIComponentForClipboard(
  part: Extract<MessagePart, { type: 'ui_component' }>,
  labels: ClipboardLabels,
): string {
  const data = part.data

  if (!data) {
    return ''
  }

  if (data.type === 'pills_list') {
    const optionsText = data.options.map(opt => `- ${opt.label}`).join('\n')
    return `${labels.options}:\n${optionsText}`.trim()
  }

  if (data.type === 'cards_list') {
    const optionsText = data.options
      .map(opt => `- ${(opt.emoji ? `${opt.emoji} ` : '') + opt.title}${opt.description ? `: ${opt.description}` : ''}`)
      .join('\n')
    return `${labels.options}:\n${optionsText}`.trim()
  }

  if (data.type === 'hotel') {
    const offers = data.offers ?? []
    const optionsText = offers.map((offer) => `- ${offer.name}`).join('\n')
    const titleText = data.searchQuery ? `${data.searchQuery}\n` : ''
    return `${titleText}${optionsText}`.trim()
  }

  // location_entities are map-only data, not useful in clipboard
  if (data.type === 'location_entities') {
    return ''
  }

  try {
    return JSON.stringify(data)
  } catch {
    return ''
  }
}

function partsToClipboardText(
  parts: Array<MessagePart>,
  labels: ClipboardLabels,
): string {
  return parts
    .map((p) => {
      if (p.type === 'text') {
        return stripMarkdown(p.content || '')
      }
      if (p.type === 'trip_plan_content') {
        return stripMarkdown(p.content || '')
      }
      if (p.type === 'ui_component') {
        return serializeUIComponentForClipboard(p, labels)
      }
      return ''
    })
    .filter(Boolean)
    .join('\n\n')
    .trim()
}

export function useClipboard({ lastAssistantMessage, isDevMode }: UseClipboardArgs) {
  const intl = useIntl()
  const [copiedRecently, setCopiedRecently] = useState(false)
  const copyTimeoutRef = useRef<number | undefined>(undefined)
  const optionsLabel = intl.formatMessage({
    id: 'EV-n0C4MNPFNHABFugxT',
    defaultMessage: 'Options',
    description: 'Heading label used when copying selectable chat options to the clipboard in trip planner',
  })

  const handleCopyResponse = useCallback(() => {
    if (!lastAssistantMessage) {
      return
    }
    const partsText = partsToClipboardText(lastAssistantMessage.parts, {
      options: optionsLabel,
    })
    const rawText = isDevMode && lastAssistantMessage.rawContent ? lastAssistantMessage.rawContent : ''
    const textToCopy = rawText
      ? [rawText.trim(), partsText].filter(Boolean).join('\n\n')
      : partsText
    navigator.clipboard?.writeText(textToCopy)
      .then(() => {
        setCopiedRecently(true)
        if (copyTimeoutRef.current) {
          window.clearTimeout(copyTimeoutRef.current)
        }
        copyTimeoutRef.current = window.setTimeout(() => {
          setCopiedRecently(false)
          copyTimeoutRef.current = undefined
        }, 1000)
      })
      .catch(() => { })
  }, [isDevMode, lastAssistantMessage, optionsLabel])

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        window.clearTimeout(copyTimeoutRef.current)
      }
    }
  }, [])

  return { copiedRecently, handleCopyResponse }
}
